<template>
  <PageLayout :title="$t('analytics.title')" :subtitle="$t('analytics.subtitle')">
    <template #actions>
      <button class="btn btn-primary" :disabled="syncing" @click="onSync">
        <i class="fas fa-sync mr-1" :class="{ 'fa-spin': syncing }"></i>
        {{ syncing ? $t('analytics.syncing') : $t('analytics.sync') }}
      </button>
    </template>

    <div class="analytics-summary mb-30">
      <div v-for="card in statCards" :key="card.label" class="analytics-summary__item">
        <span class="analytics-summary__label">{{ card.label }}</span>
        <strong class="analytics-summary__value">{{ formatNumber(card.value) }}</strong>
      </div>
    </div>

    <div v-if="accounts.length" class="row mb-30">
      <div class="col-12">
        <ContentCard>
          <h5 class="mb-3">{{ $t('analytics.account_performance') }}</h5>
          <div class="table-responsive">
            <table class="table table-sm">
              <thead>
                <tr>
                  <th>{{ $t('global.account') }}</th>
                  <th>{{ $t('global.followers') }}</th>
                  <th>{{ $t('analytics.posts_tracked') }}</th>
                  <th>{{ $t('analytics.total_impressions') }}</th>
                  <th>{{ $t('analytics.total_reach') }}</th>
                  <th>{{ $t('analytics.profile_views') }}</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="acc in accounts" :key="acc.account_id">
                  <td>@{{ acc.username }}</td>
                  <td>{{ formatNumber(acc.followers) }}</td>
                  <td>{{ acc.posts_tracked }}</td>
                  <td>{{ formatNumber(acc.impressions) }}</td>
                  <td>{{ formatNumber(acc.reach) }}</td>
                  <td>{{ formatNumber(acc.profile_views) }}</td>
                </tr>
              </tbody>
            </table>
          </div>
        </ContentCard>
      </div>
    </div>

    <ContentCard>
      <h5 class="mb-3">{{ $t('analytics.all_posts') }}</h5>
      <div v-if="loading" class="state-loading" role="status" aria-live="polite" aria-busy="true">{{ $t('analytics.loading') }}</div>
      <div v-else-if="posts.length === 0" class="text-center py-5 text-muted">
        <i class="fas fa-chart-bar fa-3x mb-3"></i>
        <p class="mb-1">{{ $t('analytics.empty') }}</p>
        <p class="small mb-3">{{ $t('analytics.empty_hint') }}</p>
        <div class="d-flex flex-wrap justify-content-center gap-2">
          <router-link to="/posts" class="btn btn-outline-primary btn-sm">{{ $t('nav.posts') }}</router-link>
          <button class="btn btn-primary btn-sm" :disabled="syncing" @click="onSync">
            {{ $t('analytics.sync') }}
          </button>
        </div>
      </div>
      <div v-else class="table-responsive">
        <table class="table table-hover">
          <thead>
            <tr>
              <th>{{ $t('analytics.post') }}</th>
              <th>{{ $t('global.account') }}</th>
              <th>{{ $t('global.type') }}</th>
              <th>{{ $t('analytics.total_impressions') }}</th>
              <th>{{ $t('analytics.total_reach') }}</th>
              <th>{{ $t('posts.engagement') }}</th>
              <th>{{ $t('posts.likes') }}</th>
              <th>{{ $t('posts.comments') }}</th>
              <th>{{ $t('posts.saves') }}</th>
              <th>{{ $t('analytics.shares') }}</th>
              <th>{{ $t('analytics.last_synced') }}</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="item in posts" :key="item.id">
              <td class="text-truncate" style="max-width: 180px">
                <a v-if="item.permalink" :href="item.permalink" target="_blank" rel="noopener">
                  {{ truncate(item.caption, 50) }}
                  <i class="fas fa-external-link-alt fa-xs ml-1"></i>
                </a>
                <span v-else>{{ truncate(item.caption, 50) }}</span>
              </td>
              <td>@{{ item.social_account?.username || '-' }}</td>
              <td><span class="badge badge-light text-capitalize">{{ item.media_type }}</span></td>
              <td><strong>{{ formatNumber(item.impressions) }}</strong></td>
              <td>{{ formatNumber(item.reach) }}</td>
              <td>{{ formatNumber(item.engagement) }}</td>
              <td>{{ formatNumber(item.likes) }}</td>
              <td>{{ formatNumber(item.comments) }}</td>
              <td>{{ formatNumber(item.saves) }}</td>
              <td>{{ formatNumber(item.shares) }}</td>
              <td class="text-muted small">{{ formatDate(item.synced_at) }}</td>
            </tr>
          </tbody>
        </table>
      </div>
    </ContentCard>
  </PageLayout>
</template>

<script>
import { mapActions, mapGetters } from 'vuex'
import i18nPage from '@/mixins/i18nPage'

export default {
  mixins: [i18nPage],
  computed: {
    ...mapGetters('analytics', ['overview', 'accounts', 'posts', 'loading', 'syncing']),
    statCards() {
      const o = this.overview || {}
      return [
        { label: this.$t('analytics.total_impressions'), value: o.total_impressions, icon: 'fas fa-eye', tone: 'primary' },
        { label: this.$t('analytics.total_reach'), value: o.total_reach, icon: 'fas fa-users', tone: 'info' },
        { label: this.$t('analytics.total_engagement'), value: o.total_engagement, icon: 'fas fa-heart', tone: 'success' },
        { label: this.$t('analytics.posts_tracked'), value: o.total_posts, icon: 'fas fa-image', tone: 'warning' },
      ]
    },
  },
  async created() {
    await this.fetchOverview()
    await this.fetchPosts()
  },
  methods: {
    ...mapActions('analytics', ['fetchOverview', 'fetchPosts', 'sync']),
    async onSync() {
      try {
        const result = await this.sync()
        await this.$appAlert(this.$t('analytics.synced', { count: result.synced }))
      } catch (e) {
        await this.$appAlert(e.response?.data?.message || this.$t('analytics.sync_failed'))
      }
    },
    formatNumber(n) {
      if (n == null) return '0'
      if (n >= 1000000) return (n / 1000000).toFixed(1) + 'M'
      if (n >= 1000) return (n / 1000).toFixed(1) + 'K'
      return n.toLocaleString(this.intlLocale)
    },
    formatDate(date) {
      if (!date) return '-'
      return new Date(date).toLocaleString(this.intlLocale)
    },
    truncate(text, len) {
      if (!text) return '-'
      return text.length > len ? text.slice(0, len) + '...' : text
    },
  },
}
</script>

<style scoped>
.analytics-summary {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 1px;
  background: var(--wc-border);
  border: 1px solid var(--wc-border);
  border-radius: var(--wc-radius-lg);
  overflow: hidden;
}

.analytics-summary__item {
  background: var(--wc-bg-card);
  padding: 1rem 1.25rem;
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
}

.analytics-summary__label {
  font-size: 0.8125rem;
  color: var(--wc-text-muted);
}

.analytics-summary__value {
  font-size: 1.35rem;
  color: var(--wc-text-heading);
}

@media (min-width: 768px) {
  .analytics-summary {
    grid-template-columns: repeat(4, 1fr);
  }
}
</style>
